<template>
  <section class="app-transporte">
    <v-container grid-list-xl>

      <!-- ALERTA DE COTA -->
      <v-alert
        v-if="cotaAtingida"
        type="warning"
        outlined
        class="mb-3"
      >
        Limite de vereadores da Câmara atingido.
      </v-alert>

      <!-- FILTRO -->
      <v-form class="app-form mb-4">
        <v-expansion-panels class="app-expansion-panels">
          <v-expansion-panel>
            <v-expansion-panel-header class="app-expansion-panel-header">
              <h4 class="text mb-0">
                <v-icon class="mr-2">filter_alt</v-icon>
                <span>Filtrar Vereadores</span>
              </h4>
            </v-expansion-panel-header>

            <v-expansion-panel-content class="app-expansion-panel-content">
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    label="Nome"
                    outlined
                    dense
                    v-model="filtros.nome"
                    @input="onFiltroChange"
                  />
                </v-col>

                <v-col cols="12" md="4">
                  <v-text-field
                    label="CPF"
                    outlined
                    dense
                    v-model="filtros.cpf"
                    v-mask="'###.###.###-##'"
                    @input="onFiltroChange"
                  />
                </v-col>

                <v-col cols="12" md="4">
                  <v-text-field
                    label="Placa do Veículo"
                    outlined
                    dense
                    v-model="filtros.placa"
                    @input="onPlacaInput"
                    @keydown="bloquearCaracterInvalido"
                  />
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    label="ID do Vereador(a)"
                    outlined
                    dense
                    v-model="filtros.id"
                    @input="onFiltroChange"
                  />
                </v-col>

                <v-col cols="12" md="4">
                  <v-select
                    label="Situação"
                    :items="situacoesMandato"
                    outlined
                    dense
                    v-model="filtros.situacaoMandato"
                    @change="onFiltroChange"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    label="Início do Mandato"
                    type="date"
                    outlined
                    dense
                    v-model="filtros.dataInicioMandato"
                    @change="onFiltroChange"
                  />
                </v-col>

                <v-col cols="12" md="4">
                  <v-text-field
                    label="Fim do Mandato"
                    type="date"
                    outlined
                    dense
                    v-model="filtros.dataFimMandato"
                    @change="onFiltroChange"
                  />
                </v-col>
              </v-row>

              <div class="d-flex justify-end mt-3">
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      outlined
                      color="error"
                      class="mr-2"
                      @click="limparFiltros"
                    >
                      <v-icon left>delete</v-icon>
                      Limpar Filtros
                    </v-btn>
                  </template>
                  <span>Limpar filtros</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      depressed
                      color="primary"
                      @click="listarVereadores"
                    >
                      Filtrar
                    </v-btn>
                  </template>
                  <span>Aplicar filtros</span>
                </v-tooltip>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-form>

      <!-- AÇÃO -->
      <div class="d-flex justify-end mb-3">
        <v-btn
          color="primary"
          depressed
          :disabled="cotaAtingida"
          @click="showModalCriacao = true"
        >
          Novo Vereador(a)
        </v-btn>
      </div>

      <ModalNovoVereador
        v-model="showModalCriacao"
        @refresh="listarVereadores"
      />

      <!-- TABELA -->
      <v-data-table
        :headers="headers"
        :items="vereadores"
        :page.sync="pagination.page"
        :items-per-page="pagination.itemsPerPage"
        :server-items-length="pagination.totalItems"
        :footer-props="{ 'items-per-page-options': [5, 10, 20] }"
        item-key="id"
        class="vereadores-table"
        @update:page="listarVereadores"
        @update:items-per-page="onItemsPerPageChange"
        @click:row="irParaDetalhe"
      >
        <template #item.cpf="{ item }">
          {{ formatCpf(item.cpf) }}
        </template>

        <template #item.dataInicioMandato="{ item }">
          {{ formatDate(item.dataInicioMandato) }}
        </template>
        <template #item.dataFimMandato="{ item }">
          {{ formatDate(item.dataFimMandato) }}
        </template>

        <template #item.situacaoMandato="{ item }">
          <v-chip
            small
            dark
            :color="situacaoColor(item.situacaoMandato)"
          >
            {{ situacaoLabel(item.situacaoMandato) }}
          </v-chip>
        </template>

        <template #item.acoes="{ item }">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                color="primary"
                class="mr-2"
                @click.stop="visualizar(item)"
              >
                description
              </v-icon>
            </template>
            <span>Visualizar</span>
          </v-tooltip>

          <v-tooltip bottom v-if="item.situacaoMandato === SITUACAO_MANDATO_ENUM.EM_MANDATO.value">
            <template #activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                color="primary"
                class="mr-2"
                @click.stop="abrirSubstituicao(item)"
              >
                directions_car
              </v-icon>
            </template>
            <span>Substituir veículo</span>
          </v-tooltip>

          <v-tooltip bottom v-if="item.situacaoMandato === SITUACAO_MANDATO_ENUM.EM_MANDATO.value && item.veiculoAtivo && item.veiculoAtivo.id">
            <template #activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                color="primary"
                class="mr-2"
                @click.stop="downloadAutorizacaoVeicular(item)"
              >
                download
              </v-icon>
            </template>
            <span>Download autorização veicular</span>
          </v-tooltip>

          <v-tooltip bottom v-if="item.situacaoMandato === SITUACAO_MANDATO_ENUM.EM_MANDATO.value">
            <template #activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                color="red"
                class="mr-2"
                @click.stop="confirmarEncerramento(item)"
              >
                block
              </v-icon>
            </template>
            <span>Encerrar mandato</span>
          </v-tooltip>
        </template>

        <template #no-data>
          <div>Não há dados disponíveis</div>
        </template>
      </v-data-table>

      <!-- MODAIS -->
      <ModalSubstituicaoVeiculo
        v-model="showModalSubstituicao"
        :vereador="vereadorParaSubstituir"
        @substituido="onVeiculoSubstituido"
      />

      <v-dialog v-model="dialogFimMandato" max-width="500">
        <v-card>
          <v-card-title>Encerrar Mandato</v-card-title>
          <v-card-text>
            Ao encerrar o mandato, a vigência do veículo será encerrada automaticamente.
            Deseja continuar?
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <template>
              <v-btn text @click="dialogFimMandato = false">Cancelar</v-btn>
            </template>

            <template>
              <v-btn color="red" text @click="encerrarMandatoConfirmado">
                Confirmar
              </v-btn>
            </template>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </section>
</template>

<script>
import ModalNovoVereador from './components/ModalNovoVereador.vue';
import ModalSubstituicaoVeiculo from './components/ModalSubstituicaoVeiculo.vue';
import cpvService from '@/services/cpv/cpv.service';
import GlobalStore from '@/store/modules/global';
import { SITUACAO_MANDATO_ENUM } from '@/models/cpv/situacaoMandato.enum';

export default {
  name: 'VereadoresLista',

  components: {
    ModalNovoVereador,
    ModalSubstituicaoVeiculo,
  },

  data: () => ({
    SITUACAO_MANDATO_ENUM,
    showModalCriacao: false,
    showModalSubstituicao: false,
    dialogFimMandato: false,

    vereadorParaSubstituir: null,
    vereadorSelecionado: null,

    camara: null,
    limiteVereadores: null,

    debounceTimer: null,

    filtros: {
      id: '',
      nome: '',
      cpf: '',
      placa: '',
      situacaoMandato: '',
      dataInicioMandato: '',
      dataFimMandato: '',
    },

    pagination: {
      page: 1,
      itemsPerPage: 10,
      totalItems: 0,
    },

    headers: [
      { text: 'ID', value: 'id', align: 'center' },
      { text: 'Nome', value: 'nome', align: 'center' },
      { text: 'CPF', value: 'cpf', align: 'center' },
      { text: 'Placa', value: 'placa', align: 'center' },
      { text: 'Início do Mandato', value: 'dataInicioMandato', align: 'center' },
      { text: 'Fim do Mandato', value: 'dataFimMandato', align: 'center' },
      { text: 'Situação', value: 'situacaoMandato', align: 'center' },
      { text: 'Ações', value: 'acoes', sortable: false },
    ],

    vereadores: [],
  }),

  computed: {
    situacoesMandato() {
      return Object.values(SITUACAO_MANDATO_ENUM).map(item => ({
        text: item.text,
        value: item.value,
      }));
    },

    vereadoresEmMandato() {
      return this.vereadores.filter(
        v => v.situacaoMandato === SITUACAO_MANDATO_ENUM.EM_MANDATO.value
      ).length;
    },

    cotaAtingida() {
      if (this.limiteVereadores === null) return false;
      return this.vereadoresEmMandato >= this.limiteVereadores;
    },
  },

  created() {
    this.listarVereadores();
  },

  methods: {
    bloquearCaracterInvalido(e) {
      const permitidos = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
      if (permitidos.includes(e.key)) return;

      if (!/^[a-zA-Z0-9]$/.test(e.key)) e.preventDefault();

      const atual = (this.filtros.placa || '');
      if (atual.length >= 7) e.preventDefault();
    },

    onPlacaInput(val) {
      const raw = String(val || '');

      const normalized = raw
        .replace(/[^a-zA-Z0-9]/g, '')
        .toUpperCase()
        .slice(0, 7);

      if (this.filtros.placa !== normalized) {
        this.filtros.placa = normalized;
      }

      this.onFiltroChange();
    },

    getCamaraIdFromCache() {
      const token = localStorage.getItem('cpv_token');
      if (!token) return null;

      try {
        const payload = token.split('.')[1];
        const decoded = JSON.parse(atob(payload));
        return decoded?.camaraId || null;
      } catch {
        return null;
      }
    },

    onFiltroChange() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.pagination.page = 1;
        this.listarVereadores();
      }, 300);
    },

    onItemsPerPageChange(val) {
      this.pagination.itemsPerPage = val;
      this.pagination.page = 1;
      this.listarVereadores();
    },

    listarVereadores() {
      const camaraId = this.getCamaraIdFromCache();

      cpvService.listVereadores({
        camaraId,
        ...this.filtros,
        pageIndex: this.pagination.page - 1,
        pageSize: this.pagination.itemsPerPage,
      }).subscribe({
        next: res => {
          const result = res.result;
          this.vereadores = result.data || [];
          this.pagination.totalItems = result.totalElements || 0;

          this.camara = this.vereadores[0]?.camara || null;
          this.limiteVereadores = this.camara
            ? Number(this.camara.maximoVereadores)
            : null;
        },
        error: () => {
          this.vereadores = [];
          this.pagination.totalItems = 0;
          this.camara = null;
          this.limiteVereadores = null;
        },
      });
    },

    limparFiltros() {
      this.filtros = {
        id: '',
        nome: '',
        cpf: '',
        placa: '',
        situacaoMandato: '',
        dataFimMandato: '',
      };
      this.pagination.page = 1;
      this.listarVereadores();
    },

    irParaDetalhe(item) {
      this.$router.push(`/cpv/vereadores/${item.id}`);
    },

    visualizar(item) {
      this.irParaDetalhe(item);
    },

    abrirSubstituicao(item) {
      this.vereadorParaSubstituir = item;
      this.showModalSubstituicao = true;
    },

    onVeiculoSubstituido() {
      this.showModalSubstituicao = false;
      this.vereadorParaSubstituir = null;
      this.listarVereadores();
    },

    confirmarEncerramento(item) {
      this.vereadorSelecionado = item;
      this.dialogFimMandato = true;
    },

    downloadAutorizacaoVeicular(item) {
      if (item?.situacaoMandato !== SITUACAO_MANDATO_ENUM.EM_MANDATO.value) {
        return;
      }

      const veiculoAtivoId = Number(item?.veiculoAtivo?.id);

      if (!Number.isFinite(veiculoAtivoId) || veiculoAtivoId <= 0) {
        GlobalStore.setSnackBar({
          show: true,
          type: 'warning',
          message: 'Não foi possível identificar o veículo para baixar a certidão.',
        });
        return;
      }

      cpvService.downloadAutorizacaoVeicular(veiculoAtivoId).subscribe({
        next: (blob) => {
          if (!blob) return;

          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `certidao-veiculo-${veiculoAtivoId}.pdf`;

          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        },
        error: () => {
          GlobalStore.setSnackBar({
            show: true,
            type: 'warning',
            message: 'Não foi possível baixar a autorização veicular.',
          });
        },
      });
    },

    encerrarMandatoConfirmado() {
      cpvService.endMandateById(this.vereadorSelecionado.id)
        .subscribe({
          next: () => {
            this.dialogFimMandato = false;
            this.vereadorSelecionado = null;
            this.listarVereadores();
          },
        });
    },

    formatCpf(cpf) {
      if (!cpf) return '-';
      return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    },

    formatDate(date) {
      if (!date) return '-';
      const [ano, mes, dia] = date.split('-');
      return `${dia}/${mes}/${ano}`;
    },

    getSituacaoConfig(value) {
      return SITUACAO_MANDATO_ENUM[value] || {
        text: value || '-',
        color: 'grey',
      };
    },

    situacaoLabel(value) {
      return this.getSituacaoConfig(value).text;
    },

    situacaoColor(value) {
      return this.getSituacaoConfig(value).color;
    },
  },
};
</script>

<style scoped>
.vereadores-table ::v-deep th {
  font-size: 14px;
  font-weight: 600;
}
.vereadores-table ::v-deep td {
  font-size: 14px;
}
</style>
